import { hasPreTradeRelevantChanges } from '@src/components/PreTrade/hooks/useOrderRequestChanged/hasPreTradeRelevantChanges';
import type { AppState } from '@src/providers/AppStateProvider/types';
import type { FullOrderRequest, ModifyOrderRequest } from '@src/providers/orders.types';
import {
  DateField,
  DecisionStatusEnum,
  ErrorActionEnum,
  formattedDateForSubscription,
  getTypedKeys,
  isOption,
  type IStrategyParameters,
  logger,
  type MarketDataSnapshot,
  ModeEnum,
  NotificationVariants,
  type OrderStrategy,
  OrdTypeEnum,
  ParameterTypeEnum,
  PricingMode,
  ReduceFirstEnum,
  ReduceOnlyEnum,
  type Security,
  SecurityTypesUtils,
  TimeInForceEnum,
  type ToastProps,
  UnifiedLiquidityEnum,
} from '@talos/kyoko';
import { v4 as uuid } from 'uuid';
import { ParameterKeysEnum } from '../../../tokens/orderStrategy';
import { getExpectedFillPriceAndSize } from '../../../utils/getExpectedFillPriceAndSize';
import { applyEndTimeDurationToParams } from '../../../utils/order';
import { resolveUiRequestSource } from '../../../utils/resolveUiRequestSource';
import { isCareOrWorkingOrderParentType } from '../NewRFQ/tradeCareOrder';
import { getReduceFirstSupport } from '../useGetReduceFirstSupport';
import { getReduceOnlySupport } from '../useGetReduceOnlySupport';
import { getOrdType } from '../utils';
import { selectUseTradeAllocations } from './OrderSlice';
import type { OrderFormState } from './types';
import { shouldSendDurationOnModify } from './utils';

interface BuildOrderRequestParams {
  /** Current application state. The builder reads the order form, reference data, and pre-trade tracking state from it. */
  appState: AppState;
  /** Toast sink used for user-facing request validation errors. Omitted by read-only comparison flows. */
  addToast?: (props: ToastProps) => void;
  /** Marks new-order requests as coming from the mobile layout when relevant. */
  isMobileLayout?: boolean;
  /** Latest market data used to calculate expected fill price and quantity. */
  marketDataSnapshot?: MarketDataSnapshot;
  /** Existing order id. Omit for new-order requests; provide for modify requests. */
  orderID?: string;
}

/**
 * Converts the current order form state into the websocket request payload used for submit and modify flows.
 */
export function orderSliceToOrderRequest({
  appState,
  addToast,
  isMobileLayout = false,
  marketDataSnapshot,
  orderID,
}: BuildOrderRequestParams): FullOrderRequest | ModifyOrderRequest | undefined {
  const orderState = appState.order;
  const formState = orderState.form;
  const useTradeAllocations = selectUseTradeAllocations(appState);
  const { enableUnifiedLiquidityMarketSymbols } = orderState.referenceData.settings;
  const { symbolField, sideField, orderCurrencyField, quantityField, strategyField } = formState;

  if (!symbolField.value || !sideField.value || !quantityField.value || !strategyField.value) {
    logger.error(new Error('Missing required fields for order generation'), {
      extra: {
        symbol: symbolField.value,
        side: sideField.value,
        quantity: quantityField.value,
        strategy: strategyField.value,
        orderID,
      },
    });
    return;
  }

  // Validate that the selected currency is in the list of available currencies
  if (orderCurrencyField.value !== undefined && !orderCurrencyField.availableItems.includes(orderCurrencyField.value)) {
    const currencyLabel = orderCurrencyField.value === '' ? 'Contracts' : orderCurrencyField.value;
    const allowedLabels = orderCurrencyField.availableItems.map(c => (c === '' ? 'Contracts' : c)).join(', ');

    logger.error(new Error('Currency validation failed'), {
      extra: {
        selectedCurrency: orderCurrencyField.value,
        availableCurrencies: orderCurrencyField.availableItems,
        symbol: symbolField.value.Symbol,
        side: sideField.value,
        strategy: strategyField.value.Name,
        orderID,
      },
    });

    addToast?.({
      id: uuid(),
      text: `Currency "${currencyLabel}" is not valid. Allowed currencies: ${allowedLabels}`,
      variant: NotificationVariants.Negative,
      timeout: 5000,
      dismissable: true,
    });

    return;
  }

  const clOrdId = uuid();
  const isUnifiedLiquidityEnabled =
    formState.parameters[ParameterTypeEnum.UnifiedLiquidity]?.value === UnifiedLiquidityEnum.Enabled;
  const selectedMarketAccounts = new Set(
    formState.orderMarketsField.selections.flatMap(selection => {
      if (orderState.referenceData.marketAccounts.marketAccountsByName.has(selection)) {
        return [selection];
      }

      return (
        orderState.referenceData.marketAccounts.marketAccountsByMarket
          .get(selection)
          ?.map(marketAccount => marketAccount.Name) ?? []
      );
    })
  );
  const selectedUnifiedLiquidityMarketSymbols = isUnifiedLiquidityEnabled
    ? formState.unifiedLiquidityMarketSymbols.filter(selection => selectedMarketAccounts.has(selection.marketAccount))
    : [];
  const marketSymbols = selectedUnifiedLiquidityMarketSymbols.map(selection => ({
    Market: selection.marketAccount,
    Symbol: selection.symbol,
  }));

  const ordType = getOrdType(orderState.referenceData.settings.showAllInPrices, symbolField.value, strategyField.value);

  const predictionOutcome = SecurityTypesUtils.PredictionOutcome.isProductType(symbolField.value)
    ? formState.predictionOutcome
    : undefined;

  const { expectedFillQty, expectedFillPrice } = getExpectedFillPriceAndSize({
    security: symbolField.value,
    orderCurrency: orderCurrencyField.value!,
    marketDataSnapshot,
    orderSide: sideField.value,
    orderQty: quantityField.value,
    predictionOutcome,
  });

  const request: FullOrderRequest | ModifyOrderRequest = {
    OrderID: orderID,
    Symbol: symbolField.value.Symbol,
    ClOrdID: clOrdId,
    Side: sideField.value,
    Currency: orderCurrencyField.value,
    OrderQty: quantityField.value,
    OrdType: ordType,
    Strategy: strategyField.value.Name,
    Parameters: getOrderRequestParameters(appState),
    TimeInForce: TimeInForceEnum.GoodTillCancel,
    Price: ordType !== OrdTypeEnum.Market ? formState.priceField.value : undefined,
    TransactTime: formattedDateForSubscription(new Date()),
    Group: formState.groupField?.value || '',
    Comments: formState.commentsField?.value || '',
  };

  const requestSource = !orderID ? resolveUiRequestSource({ isMobileLayout }) : undefined;
  if (requestSource) {
    Object.assign(request, { RequestSource: requestSource });
  }

  // If the care order is loaded, and we are assigned to it, set it.
  const associatedCareOrder =
    formState.assignToParentField.value && formState.parentIDField.value ? orderState.currentCareOrder : undefined;
  if (associatedCareOrder != null && formState.parentIDField.value) {
    request.ParentOrderID = formState.parentIDField.value;
    if (isCareOrWorkingOrderParentType(formState.parentType)) {
      request.ParentOrderType = formState.parentType;
    }
  }

  // For compatibility we need to send either Markets or MarketSymbols but long term we should always use
  // MarketSymbols key for unified liquidity
  if (isUnifiedLiquidityEnabled && enableUnifiedLiquidityMarketSymbols) {
    request.MarketSymbols = marketSymbols;
  } else {
    request.Markets = formState.orderMarketsField.selections;
  }
  // Resubmit/modify of order when groups are disabled will remove the group from the order.
  if (!orderState.referenceData.settings.enableGroups) {
    request.Group = '';
  }

  if (formState.priceModeField.value !== PricingMode.Default) {
    request.PricingMode = formState.priceModeField.value;
  }

  if (!isModifyOrderRequest(request) && expectedFillPrice && expectedFillQty) {
    request.ExpectedFillPrice = expectedFillPrice;
    request.ExpectedFillQty = expectedFillQty;
  }

  if (formState.legParams.value || formState.ulLegParamsField.value) {
    request.LegParams = [
      ...formState.legParams.getLegParamsForOrderSubmission(),
      ...formState.ulLegParamsField.getLegParamsForOrderSubmission(),
    ];
  }

  // Care-order assignment locks the parent SubAccount into the allocation field. Send that locked single allocation
  // as a plain SubAccount so the child order stays associated with the parent sub-account.
  if (
    associatedCareOrder?.SubAccount != null &&
    associatedCareOrder.SubAccount === formState.allocations.at(0)?.subAccountField.value
  ) {
    request.SubAccount = associatedCareOrder.SubAccount;
  } else if (useTradeAllocations && formState.allocations?.length) {
    // If the user changes or splits allocations after priming, preserve that explicit allocation intent.
    const allocations = formState.allocations.map(a => ({
      SubAccount: a.subAccountField.value!,
      Value: a.valueField.value!,
    }));
    request.Allocation = {
      ValueType: formState.allocationValueTypeField.value!,
      Allocations: allocations,
    };
  } else if (formState.allocations?.length === 1) {
    request.SubAccount = formState.allocations[0].subAccountField.value;
  }

  if (!isModifyOrderRequest(request) && formState.stagedOrderField?.value) {
    request.InitialDecisionStatus = DecisionStatusEnum.Staged;
  }

  // Attach pre-trade tracking fields to new orders only (UI-6943).
  // Skip when tracking is suspended (e.g. non-PreTrade resubmit, UI-7246).
  if (!orderID) {
    const preTradeState = appState.preTrade;
    const newOrderRequest = request as FullOrderRequest;

    if (preTradeState?.activeSessionId && !preTradeState.suspendTracking) {
      Object.assign(newOrderRequest, { PreTradeSessionID: preTradeState.activeSessionId });

      const referenceMatchesSession =
        preTradeState.referenceSessionId != null && preTradeState.referenceSessionId === preTradeState.activeSessionId;

      if (referenceMatchesSession && preTradeState.preTradeRequestId && preTradeState.referenceNOS) {
        const referenceStrategyName = (preTradeState.referenceNOS as { Strategy?: string }).Strategy;
        const strategyName = request.Strategy ?? referenceStrategyName;
        const strategy = strategyName
          ? appState.referenceData.strategies.strategiesByName.get(strategyName)
          : undefined;
        if (!hasPreTradeRelevantChanges(newOrderRequest, preTradeState.referenceNOS, strategy?.Parameters)) {
          Object.assign(newOrderRequest, { PreTradeRequestID: preTradeState.preTradeRequestId });
        }
      }
    }
  }

  // Prediction orders are submitted in user-normalized terms. Side is the user's Buy/Sell
  // intent; ExpressedOutcome tells the backend which Yes/No outcome that intent applies to.
  if (predictionOutcome != null) {
    request.ExpressedOutcome = predictionOutcome;
  }

  return request;
}

export function isModifyOrderRequest(request: FullOrderRequest | ModifyOrderRequest): request is ModifyOrderRequest {
  return 'OrderID' in request && request.OrderID != null;
}

export function orderSliceToOrderRequestForPreTrade(
  params: Omit<BuildOrderRequestParams, 'orderID'>
): FullOrderRequest | undefined {
  const request = orderSliceToOrderRequest(params);
  if (!request || isModifyOrderRequest(request)) {
    return;
  }

  // Strip submission-only tracking fields injected by orderSliceToOrderRequest
  delete request.PreTradeSessionID;
  delete request.PreTradeRequestID;

  return request;
}

function getOrderRequestParameters(appState: AppState): { [key: string]: any } {
  const orderState = appState.order;
  const formState = orderState.form;
  const security = formState.symbolField.value;
  const enableDurationSubmission = appState.referenceData.settings.enableDurationSubmission;
  const isStagedOrder = Boolean(formState.stagedOrderField?.value);
  const decisionStatus = orderState.orderBeingModified?.DecisionStatus;
  let shouldSendDuration: boolean;

  if (ParameterKeysEnum.Duration in formState.parameters) {
    // Strategy has a dedicated Duration field in the form
    shouldSendDuration = false;
  } else if (orderState.orderBeingModified) {
    // Modify: depends on DecisionStatus
    shouldSendDuration = shouldSendDurationOnModify({
      decisionStatus,
      enableDurationSubmission,
    });
  } else {
    // New order: only send Duration when enabled by feature flag or when staged
    shouldSendDuration = enableDurationSubmission || isStagedOrder;
  }

  const params = getTypedKeys(formState.parameters).reduce(
    (acc, key) => {
      // We need to send explicit null for Dates if user cleared a previously set value; if we don't send it or send undefined the previous value will be kept
      const shouldIgnoreDate =
        formState.parameters[key] instanceof DateField
          ? // TODO: key is not guaranteed to be a key of Partial<IStrategyParameters>
            !orderState.orderBeingModified?.Parameters?.[key as keyof Partial<IStrategyParameters>]
          : true;

      const shouldIgnore = !formState.parameters[key].hasValue && shouldIgnoreDate;
      const isUnifiedLiquidityEnabled =
        formState.parameters[ParameterTypeEnum.UnifiedLiquidity]?.value === UnifiedLiquidityEnum.Enabled;

      // Do not send some parameters if they are not supported
      switch (key) {
        case ParameterTypeEnum.ReduceFirst:
          if (
            formState.parameters[key].value === ReduceFirstEnum.Enabled &&
            !getReduceFirstSupport(security, isUnifiedLiquidityEnabled)
          ) {
            return acc;
          }
          break;

        case ParameterTypeEnum.ReduceOnly:
          if (
            formState.parameters[key].value === ReduceOnlyEnum.Enabled &&
            !getReduceOnlySupport(
              security,
              formState.strategyField.value,
              formState.orderMarketsField.selections,
              isUnifiedLiquidityEnabled,
              appState.referenceData.marketAccounts.marketAccountsByName,
              appState.referenceData.markets.marketsByName
            )
          ) {
            return acc;
          }
          break;
      }
      if (shouldIgnore) {
        return acc;
      }

      const paramDef = formState.strategyField.value?.Parameters.find(p => p.Name === key);
      // Primary parameters with payloadKey are UI-only toggles, never emitted directly
      if (paramDef?.isPrimary && paramDef?.payloadKey) {
        return acc;
      }
      // Symbol, Aggregation, and UnifiedLiquidity parameters that belong to a section with a
      // payloadKey primary param are nested inside that payloadKey object (handled below). Skip
      // them here so they are not also emitted at the top level of Parameters.
      // HedgeStrategy is intentionally excluded - it is emitted at the top level (handled below).
      if (
        paramDef !== undefined &&
        (paramDef.Type === ParameterTypeEnum.Symbol ||
          paramDef.Type === ParameterTypeEnum.Aggregation ||
          paramDef.Type === ParameterTypeEnum.UnifiedLiquidity) &&
        paramDef.section != null &&
        formState.strategyField.value?.getPrimaryParameterForSection(paramDef.section)?.payloadKey != null
      ) {
        return acc;
      }
      // HedgeStrategy parameters serialize the OrderStrategy object to its name string
      if (paramDef?.Type === ParameterTypeEnum.HedgeStrategy) {
        const stratValue = formState.parameters[key].value as OrderStrategy | undefined;
        acc[key] = stratValue?.Name ?? null;
        return acc;
      }

      acc[key] = formState.parameters[key].value ?? null;
      return acc;
    },
    {} as Record<string, any>
  );
  // Force this parameter on all orders
  params['ErrorAction'] = ErrorActionEnum.Pause;

  addSectionPayloadParameters(params, formState);
  addLegacyDynamicDeltaHedgeParameters(params, formState, security);

  // Swap Parameters.EndTime -> Parameters.Duration when allowed; otherwise DateField already computed EndTime from duration
  if (shouldSendDuration) {
    applyEndTimeDurationToParams(formState.parameters[ParameterKeysEnum.EndTime], params);
  }
  return params;
}

function addSectionPayloadParameters(params: Record<string, any>, formState: OrderFormState) {
  // New parameter-driven DDH approach: nest Symbol parameters under payloadKey.
  // Only runs when the current strategy has the DynamicDeltaHedgingEnabled parameter.
  const strategy = formState.strategyField.value;
  if (!strategy?.hasParameterDrivenDDH) {
    return;
  }

  strategy.Parameters.filter(p => p.isPrimary && p.payloadKey).forEach(primaryParam => {
    const primaryField = formState.parameters[primaryParam.Name];
    const isSectionEnabled = primaryField?.value === ModeEnum.Enabled;
    if (isSectionEnabled) {
      const nestedParams: Record<string, any> = {};
      strategy
        .getSectionParameters(primaryParam.section!)
        .filter(
          p =>
            !p.isPrimary &&
            (p.Type === ParameterTypeEnum.Symbol ||
              p.Type === ParameterTypeEnum.Aggregation ||
              p.Type === ParameterTypeEnum.UnifiedLiquidity)
        )
        .forEach(childParam => {
          const childField = formState.parameters[childParam.Name];
          if (childField?.hasValue) {
            if (childParam.Type === ParameterTypeEnum.Symbol) {
              nestedParams[childParam.Name] = (childField.value as Security)?.Symbol ?? '';
            } else if (childParam.Type === ParameterTypeEnum.UnifiedLiquidity) {
              // Only nest UnifiedLiquidity when explicitly Enabled; omit when Disabled (backend default).
              // Key is always 'UnifiedLiquidity' regardless of the parameter's internal Name.
              if (childField.value === UnifiedLiquidityEnum.Enabled) {
                nestedParams[ParameterTypeEnum.UnifiedLiquidity] = UnifiedLiquidityEnum.Enabled;
              }
            } else {
              // Aggregation: value is the aggregation name string
              nestedParams[childParam.Name] = childField.value ?? '';
            }
          }
        });
      params[primaryParam.payloadKey!] = nestedParams;
    }
  });
}

function addLegacyDynamicDeltaHedgeParameters(
  params: Record<string, any>,
  formState: OrderFormState,
  security: Security | undefined
) {
  const strategy = formState.strategyField.value;

  // Legacy DDH approach: only runs when the current strategy does NOT have the parameter-driven
  // DynamicDeltaHedgingEnabled parameter. Uses the explicit isDDHEnabled form state set by
  // the hardcoded DynamicDeltaHedge UI component. Remove once all strategies have
  // DynamicDeltaHedgingEnabled as a strategy parameter.
  if (!strategy?.hasParameterDrivenDDH && isOption(security) && formState.isDDHEnabled.value) {
    params['DynamicDeltaHedgeStrategy'] = formState.ddhStrategyField.value?.Name ?? '';
    const ddhStrategyParams: Record<string, string> = {
      DynamicDeltaHedgeSymbol: formState.ddhSymbolField.value?.Symbol ?? '',
    };
    const aggregation = formState.ddhAggregationField.value;
    if (aggregation) {
      ddhStrategyParams['DynamicDeltaHedgeAggregation'] = aggregation;
    }
    const unifiedLiquidity = formState.ddhUnifiedLiquidityField.value;
    if (unifiedLiquidity === UnifiedLiquidityEnum.Enabled) {
      ddhStrategyParams['UnifiedLiquidity'] = unifiedLiquidity;
    }
    params['DynamicDeltaHedgeStrategyParams'] = ddhStrategyParams;
  }
}
